import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { Search } from 'lucide-react'
import { ModelCard } from '@/components/dashboard/ModelCard'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { CLOUDMIND_MODELS } from '@/lib/api'
import { getSupabaseWithClerkAuth } from '@/lib/supabase'

type TierFilter = 'all' | 'speed' | 'standard' | 'premium' | 'media'

export default function ModelsPage() {
  const [filter, setFilter] = useState<TierFilter>('all')
  const [search, setSearch] = useState('')
  const { getToken } = useAuth()

  const { data: customer } = useQuery({
    queryKey: ['customer'],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db.from('customers').select('currency_preference').single()
      return data
    },
  })

  const currency = customer?.currency_preference || 'PHP'

  const filteredModels = CLOUDMIND_MODELS.filter(model => {
    const matchesTier = filter === 'all' || model.tier === filter
    const matchesSearch =
      !search ||
      model.name.toLowerCase().includes(search.toLowerCase()) ||
      model.id.toLowerCase().includes(search.toLowerCase()) ||
      model.description.toLowerCase().includes(search.toLowerCase())
    return matchesTier && matchesSearch
  })

  const tierCounts = {
    all: CLOUDMIND_MODELS.length,
    speed: CLOUDMIND_MODELS.filter(m => m.tier === 'speed').length,
    standard: CLOUDMIND_MODELS.filter(m => m.tier === 'standard').length,
    premium: CLOUDMIND_MODELS.filter(m => m.tier === 'premium').length,
    media: CLOUDMIND_MODELS.filter(m => m.tier === 'media').length,
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Model Marketplace</h2>
        <p className="text-sm text-gray-500 mt-1">
          Choose from {CLOUDMIND_MODELS.length} AI models across 4 tiers. All OpenAI-compatible.
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          className="pl-9"
          placeholder="Search models..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Tier filter */}
      <Tabs value={filter} onValueChange={(v) => setFilter(v as TierFilter)}>
        <TabsList>
          <TabsTrigger value="all">
            All
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-gray-200 rounded-full">{tierCounts.all}</span>
          </TabsTrigger>
          <TabsTrigger value="speed">
            <span className="text-green-600">Speed</span>
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-green-100 rounded-full text-green-700">{tierCounts.speed}</span>
          </TabsTrigger>
          <TabsTrigger value="standard">
            <span className="text-blue-600">Standard</span>
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-blue-100 rounded-full text-blue-700">{tierCounts.standard}</span>
          </TabsTrigger>
          <TabsTrigger value="premium">
            <span className="text-purple-600">Premium</span>
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-purple-100 rounded-full text-purple-700">{tierCounts.premium}</span>
          </TabsTrigger>
          <TabsTrigger value="media">
            <span className="text-orange-600">Media</span>
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-orange-100 rounded-full text-orange-700">{tierCounts.media}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Model grid */}
      {filteredModels.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg font-medium">No models found</p>
          <p className="text-sm mt-1">Try adjusting your search or filter</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredModels.map(model => (
            <ModelCard
              key={model.id}
              modelId={model.id}
              name={model.name}
              tier={model.tier}
              description={model.description}
              inputCostUSD={model.input_cost_per_1m}
              outputCostUSD={model.output_cost_per_1m}
              contextWindow={model.context_window}
              speedMs={model.speed_ms}
              supportsVision={model.supports_vision}
              currency={currency}
            />
          ))}
        </div>
      )}
    </div>
  )
}
